const DB_NAME = 'mivent_offline_db';
const DB_VERSION = 5;

let _db = null;

function _upgrade(db, oldVersion, newVersion) {
  const entityStores = [
    { name: 'Quotation', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Event', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Bill', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Project', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Service', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Note', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Checklist', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'TeamMember', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Payment', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
    { name: 'Policy', indexes: ['owner', 'updatedAt', 'version', 'serverId'] },
  ];

  for (const store of entityStores) {
    if (!db.objectStoreNames.contains(store.name)) {
      const objectStore = db.createObjectStore(store.name, { keyPath: 'localId' });
      for (const indexName of store.indexes) {
        objectStore.createIndex(indexName, indexName, { unique: false });
      }
    }
  }

  // sync_queue store
  if (!db.objectStoreNames.contains('sync_queue')) {
    const queueStore = db.createObjectStore('sync_queue', {
      keyPath: 'id',
      autoIncrement: true,
    });
    queueStore.createIndex('entityType', 'entityType', { unique: false });
    queueStore.createIndex('status', 'status', { unique: false });
    queueStore.createIndex('createdAt', 'createdAt', { unique: false });
    queueStore.createIndex('owner', 'owner', { unique: false });
  }

  // sync_metadata store
  if (!db.objectStoreNames.contains('sync_metadata')) {
    db.createObjectStore('sync_metadata', { keyPath: 'key' });
  }
}

export function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      _upgrade(event.target.result, event.oldVersion, event.newVersion);
    };
    request.onsuccess = (event) => {
      _db = event.target.result;
      resolve(_db);
    };
    request.onerror = (event) => reject(event.target.error);
  });
}

export function closeDB() {
  if (_db) { _db.close(); _db = null; }
}

export function deleteDB() {
  closeDB();
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function dbGet(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function dbGetAll(storeName, range) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = range ? store.getAll(range) : store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function dbGetAllByIndex(storeName, indexName, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const index = store.index(indexName);
    const req = index.getAll(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function dbPut(storeName, record) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.put(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function dbDelete(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function dbClear(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function dbCount(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function createEntityDB(name) {
  return {
    get: (key) => dbGet(name, key),
    getAll: () => dbGetAll(name),
    getAllByOwner: (owner) => dbGetAllByIndex(name, 'owner', owner),
    getAllByIndex: (indexName, value) => dbGetAllByIndex(name, indexName, value),
    put: (record) => dbPut(name, record),
    delete: (key) => dbDelete(name, key),
    clear: () => dbClear(name),
    count: () => dbCount(name),
  };
}

export const QuotationDB = createEntityDB('Quotation');
export const EventDB = createEntityDB('Event');
export const BillDB = createEntityDB('Bill');
export const ProjectDB = createEntityDB('Project');
export const ServiceDB = createEntityDB('Service');
export const NoteDB = createEntityDB('Note');
export const ChecklistDB = createEntityDB('Checklist');
export const TeamMemberDB = createEntityDB('TeamMember');
export const PaymentDB = createEntityDB('Payment');
export const PolicyDB = createEntityDB('Policy');

const SyncQueueStore = {
  get: (key) => dbGet('sync_queue', key),
  getAll: () => dbGetAll('sync_queue'),
  getAllByStatus: (status) => dbGetAllByIndex('sync_queue', 'status', status),
  getAllByOwner: (owner) => dbGetAllByIndex('sync_queue', 'owner', owner),
  add: (record) => {
    const r = { ...record, createdAt: record.createdAt || Date.now(), updatedAt: Date.now() };
    return dbPut('sync_queue', r);
  },
  delete: (key) => dbDelete('sync_queue', key),
  clear: () => dbClear('sync_queue'),
  count: () => dbCount('sync_queue'),
};

const SyncMetadataStore = {
  get: (key) => dbGet('sync_metadata', key),
  set: (key, value) => dbPut('sync_metadata', { key, value, updatedAt: Date.now() }),
  delete: (key) => dbDelete('sync_metadata', key),
};

export const ENTITY_STORES = {
  Quotation: QuotationDB,
  Event: EventDB,
  Bill: BillDB,
  Project: ProjectDB,
  Service: ServiceDB,
  Note: NoteDB,
  Checklist: ChecklistDB,
  TeamMember: TeamMemberDB,
  Payment: PaymentDB,
  Policy: PolicyDB,
};

export const ENTITY_API_MAP = {
  Quotation: 'quotations',
  Event: 'events',
  Bill: 'billing',
  Project: 'projects',
  Service: 'services',
  Note: 'notes',
  Checklist: 'checklists',
  TeamMember: 'team',
  Payment: 'payments',
  Policy: 'policy',
};

export const ENTITY_LIST = Object.keys(ENTITY_STORES);

export { SyncQueueStore, SyncMetadataStore };
